import React from 'react'
import { reduxForm } from 'redux-form'
import { Field } from 'redux-form'
import type { Form } from 'lib/types'
import FormButtons from 'components/FormButtons'
import { renderInputField, renderCurrencyInputField } from 'components/FormFields'
import { unsetModal } from 'lib/modal/actions'
import { setBanner, setLoad, setUnload } from 'lib/notification/actions'
import { updateLineItem } from 'lib/subaccount/actions'
import cleaners from 'lib/form/cleaners'
import validators from 'lib/form/validators'
import styles from 'components/Forms/styles.css'

type Props = {
  dispatch: Function,
  form: Form,
  number: number,
  subnumber: number,
  lineItem: { id: string, effective_date: string, amount: number, memo: string },
  onSuccess?: Function,
}

const onSubmit = (data, dispatch, onSuccess, originalLineItem) => {
  // Check if data has actually changed compared to original values
  const formattedDate = data.date
  const originalDate = originalLineItem.effective_date

  const amount = data.amount !== null && data.amount !== undefined ? data.amount.toString() : ''

  const hasChanged = formattedDate !== originalDate || amount !== originalLineItem.amount.toString() || data.memo !== originalLineItem.memo

  // If nothing changed, just close the modal and return
  if (!hasChanged) {
    dispatch(unsetModal())
    return Promise.resolve()
  }

  dispatch(setLoad())

  const formData = {
    ...data,
    amount: amount,
  }

  return dispatch(updateLineItem(data.number, data.subnumber, data.lineItemId, cleaners.lineItem(formData)))
    .then((response) => {
      dispatch(setBanner('Transaction successfully updated.'))
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess()
      }
    })
    .catch((error) => {
      if (error === 'EditsDisabledAfterMonthClose') {
        dispatch(setBanner('Cannot modify transactions in a closed month', 'danger'))
      } else {
        dispatch(setBanner('Transaction update failed!', 'danger'))
      }
    })
    .finally(() => {
      dispatch(unsetModal())
      dispatch(setUnload())
    })
}

const Component = ({ number, subnumber, lineItem, form, onSuccess }: Props) => {
  const handleSubmit = form.handleSubmit((data, dispatch) => {
    return onSubmit({ ...data, number, subnumber, lineItemId: lineItem.id }, dispatch, onSuccess, lineItem)
  })

  const handleCancel = () => {
    return form.dispatch(unsetModal())
  }

  return (
    <section>
      <h4 className="section">Edit Transaction</h4>
      <form onSubmit={handleSubmit}>
        <div className={styles.section}>
          <div className={styles.formItemQuarter}>
            <Field component={renderInputField} label="Date" name="date" type="date" autoFocus />
          </div>
          <div className={styles.formItemQuarter}>
            <Field component={renderCurrencyInputField} name="amount" placeholder="0.00" label="Amount" flattenValue />
          </div>
          <div className={styles.formItemHalf}>
            <Field component={renderInputField} name="memo" label="Memo" type="text" />
          </div>
        </div>
        <FormButtons {...form} onCancel={handleCancel} submitLabel="Update" />
      </form>
    </section>
  )
}

const EditLineItemForm = reduxForm({
  form: 'editLineItem',
  propNamespace: 'form',
  validate: validators.lineItem,
  enableReinitialize: true,
})((props) => <Component {...props} />)

export default EditLineItemForm
